import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import AcquisitionsAPI from "@/api/acquisitions";
import CustomFieldsApi from "@/api/custom-fields";
import { getEvidence, userApi } from "@/api";

import { CUSTOM_FIELD_CATEGORIES } from "@/pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import { CustomField } from "@/Features/CustomFields/custom-fields.types";
import { ContactsAPI, StorageAPI, User } from "@/Features";
import { ImportDefinition } from "../../_components/DataImport";
import {
  createAcquisitionCoreFields,
  createAcquisitionCustomFields,
} from "./acquisitionImport.fields";
import { importAcquisitionRecords } from "./acquisitionImport.importer";
import {
  ContactItem,
  EvidenceItem,
  FormatOption,
  HashTypeOption,
  TypeOption,
  UseAcquisitionImportDefinitionOptions,
} from "./acquisitionImport.types";

export function useAcquisitionImportDefinition(
  options: UseAcquisitionImportDefinitionOptions
): {
  definition: ImportDefinition | null;
  isLoading: boolean;
} {
  const { case_id } = options;

  const results = useQueries({
    queries: [
      {
        queryKey: [
          "users",
          "list",
          { include_observers: false, include_inactive: false },
        ],
        queryFn: () =>
          userApi.getUsers({
            include_observers: false,
            include_inactive: false,
          }),
        throwOnError: true,
      },
      {
        queryKey: ["evidence", "list", { case_id }],
        queryFn: () => getEvidence({ case_id }),
        throwOnError: true,
      },
      {
        queryKey: [
          "acquisitions:customFields:import",
          { category: CUSTOM_FIELD_CATEGORIES.ACQUISITION },
        ],
        queryFn: () =>
          CustomFieldsApi.getCustomFields({
            category: CUSTOM_FIELD_CATEGORIES.ACQUISITION,
          }),
        throwOnError: true,
      },
      {
        queryKey: ["people:contacts", { case_id }],
        queryFn: () => ContactsAPI.get({ case_id }),
        throwOnError: true,
      },
      {
        queryKey: [
          "storage:list",
          { case_id, active_only: true, include_general_storage: true },
        ],
        queryFn: () =>
          StorageAPI.getStorage({
            case_id,
            active_only: true,
            include_general_storage: true,
          }),
        throwOnError: true,
      },
      {
        queryKey: ["acquisitions:formats:list"],
        queryFn: () => AcquisitionsAPI.getFormats(),
        throwOnError: true,
      },
      {
        queryKey: ["acquisitions:types:list"],
        queryFn: () => AcquisitionsAPI.getTypes(),
        throwOnError: true,
      },
      {
        queryKey: ["acquisitions:hashTypes:list"],
        queryFn: () => AcquisitionsAPI.getHashTypes(),
        throwOnError: true,
      },
    ],
  });

  const users: User[] = useMemo(() => results[0]?.data || [], [results]);
  const evidenceItems: EvidenceItem[] = useMemo(
    () => results[1]?.data || [],
    [results]
  );
  const customFields: CustomField[] = useMemo(
    () =>
      results[2]?.data?.filter?.((field: CustomField) => field.enabled === 1) ||
      [],
    [results]
  );
  const contacts: ContactItem[] = useMemo(
    () => results[3]?.data?.data || [],
    [results]
  );
  const storageItems = useMemo(() => results[4]?.data || [], [results]);
  const formats: FormatOption[] = useMemo(
    () => results[5]?.data || [],
    [results]
  );
  const types: TypeOption[] = useMemo(() => results[6]?.data || [], [results]);
  const hashTypes: HashTypeOption[] = useMemo(
    () => results[7]?.data || [],
    [results]
  );

  const isLoading = results.some((r) => r.isLoading);

  const definition = useMemo<ImportDefinition | null>(() => {
    if (isLoading) return null;

    const fields = [
      ...createAcquisitionCoreFields({
        users,
        evidenceItems,
        contacts,
        storageItems,
        formats,
        types,
        hashTypes,
      }),
      ...createAcquisitionCustomFields(customFields),
    ];

    return {
      id: "acquisition",
      title: "Import Acquisitions from CSV",
      description:
        "Upload a CSV file to bulk import acquisitions. Map your CSV columns to acquisition fields, preview the data, and import.",
      fields,
      onImport: (records) =>
        importAcquisitionRecords({ records, case_id, customFields }),
      batchSize: 5,
    };
  }, [
    isLoading,
    case_id,
    users,
    evidenceItems,
    customFields,
    contacts,
    storageItems,
    formats,
    types,
    hashTypes,
  ]);

  return { definition, isLoading };
}
